import { useState, useEffect, useRef } from 'react';
import RavePilotHeader from '../components/RavePilotHeader.jsx';
import RavePilotFooter from '../components/RavePilotFooter.jsx';
import ClipPlayerOverlay from '../components/ClipPlayerOverlay.jsx';
import { applyBrandTheme, brandName } from '../lib/brandTheme.js';
import { rewardCopy } from '../lib/reward.js';

// Stream customer subdomain — same one used across Record.jsx / the worker.
// Part of every public Stream URL, not secret.
const STREAM_SUBDOMAIN = 'customer-zzob661ztw2o5t2h';

// Build the clip-shaped object ClipPlayerOverlay expects from a Stream UID.
const landingClip = (uid) => ({
  iframe_url: `https://${STREAM_SUBDOMAIN}.cloudflarestream.com/${uid}/iframe?preload=metadata`,
  thumbnail_url: `https://${STREAM_SUBDOMAIN}.cloudflarestream.com/${uid}/thumbnails/thumbnail.jpg?time=2s&height=400`,
  watch_url: `https://${STREAM_SUBDOMAIN}.cloudflarestream.com/${uid}/watch`,
});

// Format a full name as "First L." for public display — first name plus a
// last initial. Falls back to just the first name when there's no last name.
const firstNameLastInitial = (full) => {
  const parts = String(full || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '';
  if (parts.length === 1) return parts[0];
  return `${parts[0]} ${parts[parts.length - 1][0].toUpperCase()}.`;
};

// Real approved customer testimonials featured on the public landing page.
// Swap in real pull-quotes from the videos when ready.
const LANDING_TESTIMONIALS = [
  {
    name: firstNameLastInitial('Dan'),
    title: 'Why I switched to RavePilot',
    blurb: 'What finally made the switch worth it, in his own words.',
    duration: '0:55',
    clip: landingClip('24aeb059bb7b2ea70cd933f9c0dee08a'),
  },
  {
    name: firstNameLastInitial('Taylor Gray'),
    title: 'My RavePilot story',
    blurb: 'Why he made the call, and what changed after.',
    duration: '0:41',
    clip: landingClip('278346403cfbf50b0a589ea7ff2bc7de'),
  },
];

// Hide the testimonial strip until we have real RavePilot videos to show.
const SHOW_TESTIMONIALS = false;

// Landing page — what visitors see at the bare domain (no /r/:token).
//
// Two surfaces:
//   1. Marketing-style hero (left text, right floating gift cards)
//   2. Magic-link request card: customer types their 10-digit RavePilot
//      landline number. We POST to /api/landing/request-link, which
//      looks them up in HubSpot via the custom number object and emails
//      the magic link to every contact email on file. Worker uses
//      HubSpot's transactional single-email API (template 213062522201)
//      with {{ custom.link }} + {{ contact.firstname }}.
//
// Phone is the right input because:
//   - Boomers know their RavePilot landline number cold (it's printed on
//     their bills and on RavePilot marketing material).
//   - Email addresses get mistyped constantly — losing a "magic link
//     to your inbox" because the customer typed gmial.com is bad UX.
//   - The progress bar / autosubmit-at-10-digits flow is much friendlier
//     than free-form email validation.
//
// The worker's response is honest, not anti-enumeration (the RavePilot
// customer base is small enough that "we couldn't find you" is more
// useful than hiding existence — same trade ravepilotportal makes).

export default function Landing() {
  const [email, setEmail] = useState('');
  const [state, setState] = useState('idle'); // idle | sending | sent | error
  const [errorMsg, setErrorMsg] = useState('');
  const [playingClip, setPlayingClip] = useState(null);
  const seed = (typeof window !== 'undefined' && window.__BRAND__) || null;
  const [brand, setBrand] = useState(seed?.brand || null);
  const [cents, setCents] = useState({ approval: seed?.approval_payout_cents || 0, usage: seed?.usage_bonus_cents || 0 });
  const inputRef = useRef(null);

  // Resolve the brand for THIS host (custom domain → tenant). Applies the
  // theme (accent color, title) and drives the reward-aware copy below, so
  // stories.voiply.com renders as Voiply and share.ravepilot.com as RavePilot.
  useEffect(() => {
    let alive = true;
    if (seed?.brand) applyBrandTheme(seed.brand);   // already injected server-side; keeps things consistent
    fetch('/api/landing/config')
      .then((r) => r.json())
      .then((d) => {
        if (!alive || !d || !d.ok) return;
        setBrand(d.brand);
        setCents({ approval: d.approval_payout_cents || 0, usage: d.usage_bonus_cents || 0 });
        applyBrandTheme(d.brand);
      })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  // Reward + name copy. rewardCopy expects a collector-shaped object; the
  // landing config returns the brand directly, so wrap it. Falls back to
  // RavePilot custom-reward defaults until the config loads.
  const bn = brandName(brand);
  const rw = rewardCopy({ brand: brand || {},
    approval_payout_cents: cents.approval, usage_bonus_cents: cents.usage });

  const valid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

  const submit = async (e) => {
    e?.preventDefault();
    if (!valid || state === 'sending') return;
    setState('sending');
    setErrorMsg('');
    try {
      const res = await fetch('/api/landing/request-link', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim().toLowerCase() }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.ok) {
        setState('sent');
      } else {
        setState('error');
        setErrorMsg(data.error || 'Something went wrong. Please try again.');
      }
    } catch {
      setState('error');
      setErrorMsg('Network hiccup — please try again.');
    }
  };

  return (
    <div className="vt-app min-h-screen flex flex-col">
      <RavePilotHeader brand={brand} />
      <main className="vt-landing-page flex-1 w-full max-w-[880px] mx-auto px-4 md:px-6 py-6 md:py-8">
        {/* Hero — text left, free-month badge right */}
        <div className="vt-landing-hero animate-fade-in-up">
          <div className="vt-landing-hero-content">
            <div className="vt-eyebrow"><span className="vt-bar"></span>Be one of our first customers</div>
            <h1 className="vt-h-display vt-landing-title">
              Tell us your {bn} story.<br />
              <span className="vt-landing-title-accent">
                {rw.type === 'cash' ? `Earn ${rw.heroAmount} per video.` : 'Your first month is on us.'}
              </span>
            </h1>
            <p className="vt-lead vt-landing-lede">
              Up to four short videos, recorded right here in your browser. About 15 minutes total.
              Share your story and you'll get{' '}
              <strong className="vt-muted-strong">{rw.welcomeLede}</strong>.
            </p>
          </div>

          <div className="vt-landing-hero-art" aria-hidden="true">
            <div className="vt-gift-card vt-gc-1">
              <div className="vt-gc-brand">{rw.type === 'cash' ? rw.heroAmount : '1 month'}</div>
              <div className="vt-gc-amount">&nbsp;</div>
            </div>
            <div className="vt-gift-card vt-gc-2">
              <div className="vt-gc-brand">{rw.type === 'cash' ? `${rw.heroAmount} \u00b7 per video` : 'First customer'}</div>
              <div className="vt-gc-amount">Reward</div>
            </div>
            <div className="vt-gift-card vt-gc-3">
              <div className="vt-gc-brand">Welcome aboard</div>
              <div className="vt-gc-amount">Story</div>
            </div>
            <div className="vt-hero-glow"></div>
          </div>
        </div>

        {/* Magic-link card */}
        <div className="vt-paper vt-magic-card animate-fade-in-up" style={{ animationDelay: '120ms' }}>
          {state !== 'sent' ? (
            <>
              <div className="vt-magic-icon-tile" aria-hidden="true">
                {/* Mail icon */}
                <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="2" y="4" width="20" height="16" rx="2"/>
                  <path d="M22 7l-10 6L2 7"/>
                </svg>
              </div>

              <h2 className="vt-magic-title">Get your personal link</h2>
              <p className="vt-magic-sub">
                Enter your email address and we'll send your personal recording link straight to your inbox.
              </p>

              <form className="vt-magic-form" onSubmit={submit} noValidate>
                <div className="vt-phone-input-wrapper">
                  <input
                    ref={inputRef}
                    className={'vt-phone-main-input' + (valid ? ' complete' : '')}
                    type="email"
                    inputMode="email"
                    autoComplete="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      if (state === 'error') { setState('idle'); setErrorMsg(''); }
                    }}
                    disabled={state === 'sending'}
                  />
                  {valid && state !== 'sending' && (
                    <div className="vt-phone-check" aria-hidden="true">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12"/>
                      </svg>
                    </div>
                  )}
                </div>

                <button
                  type="submit"
                  className="vt-btn vt-btn-primary vt-btn-lg vt-tap vt-magic-submit"
                  disabled={!valid || state === 'sending'}
                >
                  {state === 'sending' ? (
                    <>
                      <span className="vt-submit-spinner" aria-hidden="true"></span>
                      Sending your link…
                    </>
                  ) : (
                    <>
                      Send my link
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <polyline points="9 5 16 12 9 19"/>
                      </svg>
                    </>
                  )}
                </button>

                {state === 'error' && errorMsg && (
                  <div className="vt-magic-error">{errorMsg}</div>
                )}
              </form>
            </>
          ) : (
            <div className="vt-magic-success">
              <span className="vt-magic-success-ico">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" width="32" height="32">
                  <polyline points="20 6 9 17 4 12"/>
                </svg>
              </span>
              <h2 className="vt-magic-title" style={{ margin: '6px 0' }}>Check your email in a minute.</h2>
              <p className="vt-magic-sub">
                We sent your personal link to <strong>{email}</strong>.
                Tap it from your inbox and you'll come right back here, ready to record.
              </p>
              <button
                type="button"
                className="vt-link-button vt-tap"
                onClick={() => {
                  setState('idle');
                  setEmail('');
                  setErrorMsg('');
                  setTimeout(() => inputRef.current?.focus(), 50);
                }}
              >
                Use a different email
              </button>
            </div>
          )}
        </div>

        {/* Real customer testimonials — hidden via SHOW_TESTIMONIALS until we
            have real RavePilot videos. Plays back via shared ClipPlayerOverlay. */}
        {SHOW_TESTIMONIALS && (
        <div className="vt-examples-strip animate-fade-in-up" style={{ animationDelay: '160ms' }}>
          <div className="vt-examples-head">
            <div className="vt-eyebrow" style={{ justifyContent: 'center' }}>
              <span className="vt-bar"></span>Hear from real customers
            </div>
            <h3 className="vt-examples-title">See how others told their story</h3>
            <p className="vt-examples-sub">Real RavePilot customers. About a minute each.</p>
          </div>
          <div className="vt-examples-row">
            {LANDING_TESTIMONIALS.map((ex, i) => (
              <button
                key={ex.title}
                type="button"
                className="vt-example-card vt-tap"
                onClick={() => setPlayingClip(ex.clip)}
                aria-label={`Play testimonial: ${ex.title}`}
              >
                <span className="vt-example-thumb" data-shade={i === 0 ? 'warm' : 'cool'}>
                  <img
                    className="vt-example-poster"
                    src={ex.clip.thumbnail_url}
                    alt=""
                    loading="lazy"
                    onError={(e) => { e.currentTarget.style.display = 'none'; }}
                  />
                  <span className="vt-example-portrait" aria-hidden="true">
                    <span className="vt-ep-head"></span>
                    <span className="vt-ep-shoulders"></span>
                  </span>
                  <span className="vt-example-play">
                    <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
                      <path d="M6 4l14 8-14 8z" fill="currentColor" />
                    </svg>
                  </span>
                  <span className="vt-example-dur">{ex.duration}</span>
                </span>
                <span className="vt-example-meta">
                  <span className="vt-example-name">{ex.name}</span>
                  <span className="vt-example-quote">{ex.blurb}</span>
                </span>
              </button>
            ))}
          </div>
        </div>
        )}

        {/* How it works */}
        <div className="vt-howit-strip animate-fade-in-up" style={{ animationDelay: '200ms' }}>
          <div className="vt-howit-strip-head">
            <div className="vt-eyebrow" style={{ justifyContent: 'center' }}>
              <span className="vt-bar"></span>How it works
            </div>
            <h3 className="vt-howit-strip-title">
              Pick stories, read them out loud, get your free month.
            </h3>
          </div>
          <div className="vt-howit">
            <div className="vt-howit-card">
              <div className="vt-howit-num"><span className="vt-howit-n">1</span>Pick</div>
              <div className="vt-howit-title">Choose your stories</div>
              <div className="vt-howit-body">A few short scripts with fill-in-the-blanks. Nothing to memorize.</div>
            </div>
            <div className="vt-howit-card">
              <div className="vt-howit-num"><span className="vt-howit-n">2</span>Record</div>
              <div className="vt-howit-title">Press record, read the words</div>
              <div className="vt-howit-body">A teleprompter sits right beside your camera. Redo as many times as you like.</div>
            </div>
            <div className="vt-howit-card">
              <div className="vt-howit-num"><span className="vt-howit-n">3</span>{rw.type === 'cash' ? 'Get paid' : 'Get your month'}</div>
              <div className="vt-howit-title">{rw.type === 'cash' ? rw.thanksStepTitle : 'Free month, activated'}</div>
              <div className="vt-howit-body">We review within 2 business days, then {rw.thanksStepBody}.</div>
            </div>
          </div>
        </div>

        <p className="vt-landing-foot">
          Looking for RavePilot home phone service? <a href="https://ravepilot.com">Visit ravepilot.com</a>.
        </p>
      </main>
      <RavePilotFooter brand={brand} />
      {playingClip && (
        <ClipPlayerOverlay
          clip={playingClip}
          script={{ title: 'Customer testimonial' }}
          onClose={() => setPlayingClip(null)}
        />
      )}
    </div>
  );
}
